// European (NL) formatting helpers. No external date library.
// Amounts are integer cents; weights are integer kilograms.

const TZ = "Europe/Amsterdam";

const dfDate = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  timeZone: TZ,
});
const dfDayTime = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: TZ,
});
const dfLongDate = new Intl.DateTimeFormat("nl-NL", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: TZ,
});
const nfMoney = new Intl.NumberFormat("nl-NL", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const nfWeight = new Intl.NumberFormat("nl-NL", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
// Tariff-line quantities are fractional in TALIS (7,5 uur · 1,25 uur).
const nfQty = new Intl.NumberFormat("nl-NL", { maximumFractionDigits: 3 });

const EM_DASH = "—";

/** "14 mei 2026" */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return EM_DASH;
  return dfDate.format(new Date(iso));
}

/** "14 mei · 09:12" (timeline) */
export function fmtDayTime(iso: string): string {
  const s = dfDayTime.format(new Date(iso));
  // Intl renders "14 mei 09:12" — insert the middot separator used in the design.
  return s.replace(/(\d{2}:\d{2})$/, "· $1").replace(/\s·/, " ·");
}

/** "woensdag 13 mei 2026" */
export function fmtLongDate(iso: string): string {
  return dfLongDate.format(new Date(iso));
}

/** integer cents → "€ 24.750,00" */
export function fmtMoney(
  cents: number | null | undefined,
  currency = "EUR",
): string {
  if (cents == null) return EM_DASH;
  const symbol = currency === "EUR" ? "€" : currency;
  return `${symbol} ${nfMoney.format(cents / 100)}`;
}

/** integer kilograms → "48,0 t" */
export function fmtWeight(kg: number | null | undefined): string {
  if (kg == null) return EM_DASH;
  return `${nfWeight.format(kg / 1000)} t`;
}

/** fractional quantity → "7,5" · "1.234" (no trailing zeros). */
export function fmtQty(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  return nfQty.format(n);
}

/**
 * fractional quantity → "7,5" for an editable input. No thousands grouping:
 * `parseQty` reads a lone dot as the decimal separator, so a grouped "1.000"
 * would not round-trip.
 */
export function qtyToStr(n: number | null | undefined): string {
  if (n == null) return "";
  return String(n).replace(".", ",");
}

/**
 * "7,5" / "7.5" / "1.234,5" → number (or null if unparseable).
 *
 * A comma is the decimal separator and dots group thousands ("1.234,5"). With
 * no comma a lone dot is read as the DECIMAL separator ("7.5" → 7,5): a
 * quantity is far more often fractional than in the thousands, and reading the
 * dot as a group separator would silently inflate the line tenfold.
 */
export function parseQty(input: string): number | null {
  const s = input.trim().replace(/\s/g, "");
  if (s === "" || s === "-") return null;
  const normalised = s.includes(",") ? s.replace(/\./g, "").replace(",", ".") : s;
  const n = Number(normalised);
  return Number.isFinite(n) ? n : null;
}

/** "Tata Steel, IJmuiden" — company and town, whichever of the two is known. */
export function fmtPlace(
  company: string | null,
  address: string | null,
  postalCode: string | null,
  city: string | null,
): string | null {
  const town = [postalCode?.trim(), city?.trim()].filter(Boolean).join(" ");
  return [company?.trim(), address?.trim(), town].filter(Boolean).join(", ") || null;
}

/** "A → B" when the work moved between two places, otherwise just the location. */
export function fmtRoute(from: string | null, to: string | null): string {
  if (from && to) return `${from} → ${to}`;
  return from ?? to ?? EM_DASH;
}

/** "26 jun – 27 jun", collapsed to one date when they are the same. */
export function fmtDateRange(from: string | null, to: string | null): string {
  if (from && to) return from === to ? fmtDate(to) : `${fmtDate(from)} – ${fmtDate(to)}`;
  return from || to ? fmtDate(from ?? to) : EM_DASH;
}

/** minutes → "5:30" (h:mm), for tariff-line hour totals. */
export function fmtHours(min: number | null | undefined): string {
  if (min == null) return EM_DASH;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

/** integer cents → "1.234,56" (no symbol) for editable currency inputs. */
export function centsToEuroStr(cents: number | null | undefined): string {
  if (cents == null) return "";
  return nfMoney.format(cents / 100);
}

/** "1.234,56" / "1234.56" / "1234,56" → integer cents (or null if unparseable). */
export function parseEurosToCents(input: string): number | null {
  const cleaned = input.trim().replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  if (cleaned === "" || cleaned === "-") return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

/** "2026-W21" → { week: "21", year: "2026" } for the "week 21 · 2026" pattern. */
export function parseWeek(
  requestedWeek: string | null | undefined,
): { week: string; year: string } | null {
  if (!requestedWeek) return null;
  const m = requestedWeek.match(/^(\d{4})-W(\d{1,2})$/);
  if (!m) return null;
  return { year: m[1]!, week: m[2]! };
}
